import React, { useState, useRef } from "react";
import { Field, ErrorMessage } from "formik";
import { useTranslation } from "react-i18next";
import {
  FaBuilding,
  FaFilePdf,
  FaFileWord,
  FaShieldAlt,
  FaCertificate,
  FaLeaf,
  FaUserShield,
  FaLock,
} from "react-icons/fa";

const Step2 = () => {
  const { t } = useTranslation();
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherValue, setOtherValue] = useState("");
  const selectRef = useRef(null);

  const TRADE_OPTIONS = [
    {
      value: "roofing",
      label: t("contractorSignup.form.step2.tradeOptions.roofing", "Roofing"),
    },
    {
      value: "façade",
      label: t("contractorSignup.form.step2.tradeOptions.facade", "Facade"),
    },
    {
      value: "plumbing",
      label: t("contractorSignup.form.step2.tradeOptions.plumbing", "Plumbing"),
    },
    {
      value: "electrical",
      label: t("contractorSignup.form.step2.tradeOptions.electrical", "Electrical"),
    },
    { value: "other", label: t("contractorSignup.form.step2.tradeOptions.other", "Other") },
  ];

  return (
    <div className="grid grid-cols-2 gap-6 bg-[#F6FBFF] custom-scrollbar p-4 max-h-[400px] overflow-y-auto">
      {/* Sustainability & ESG */}
      <div className="col-span-2">
        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
          {t("contractorSignup.form.step2.sustainabilityLabel", "Sustainability & ESG")}
        </label>
        <Field name="sustainability_esg">
          {({ form, field }) => (
            <div>
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={(event) => {
                  form.setFieldValue("sustainability_esg", event.currentTarget.files[0]);
                }}
                className="bg-[#F1F6F9] border border-gray-300 rounded px-2 py-1 w-full text-sm focus:border-green-500 focus:outline-none transition"
              />
              <ErrorMessage
                name="sustainability_esg"
                component="div"
                className="text-red-500 text-xs mt-1"
              />
              <div className="mt-2 text-xs text-gray-600 flex gap-2 flex-wrap">
                {field.value ? (
                  <span className="flex items-center gap-1">
                    {field.value.name.endsWith(".pdf") ? (
                      <FaFilePdf className="text-red-600" />
                    ) : (
                      <FaFileWord className="text-blue-600" />
                    )}
                    {field.value.name}
                  </span>
                ) : (
                  <span>{t("contractorSignup.form.noFileUploaded", "No file uploaded.")}</span>
                )}
              </div>
            </div>
          )}
        </Field>
      </div>
      {/* Insurance & Liability */}
      <div className="col-span-2">
        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
          {t("contractorSignup.form.step2.insuranceLabel", "Insurance & Liability")}
          <span className="text-red-500">*</span>
        </label>
        <Field name="insurance_liability">
          {({ form, field }) => (
            <div>
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={(event) => {
                  form.setFieldValue("insurance_liability", event.currentTarget.files[0]);
                }}
                className="bg-[#F1F6F9] border border-gray-300 rounded px-2 py-1 w-full text-sm focus:border-green-500 focus:outline-none transition"
              />
              <ErrorMessage
                name="insurance_liability"
                component="div"
                className="text-red-500 text-xs mt-1"
              />
              <div className="mt-2 text-xs text-gray-600 flex gap-2 flex-wrap">
                {field.value ? (
                  <span className="flex items-center gap-1">
                    {field.value.name.endsWith(".pdf") ? (
                      <FaFilePdf className="text-red-600" />
                    ) : (
                      <FaFileWord className="text-blue-600" />
                    )}
                    {field.value.name}
                  </span>
                ) : (
                  <span>{t("contractorSignup.form.noFileUploaded", "No file uploaded.")}</span>
                )}
              </div>
            </div>
          )}
        </Field>
      </div>
      {/* Trade/Specialization Dropdown */}
      <div className="col-span-2">
        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
          {t("contractorSignup.form.step2.tradeLabel", "Trade / Specialization")}
          <span className="text-red-500">*</span>
        </label>
        <Field name="trade_specialization">
          {({ form, field }) => {
            const fieldSelectedTrades = field.value
              ? field.value.split(",").map((v) => v.trim())
              : [];

            const handleSelectChange = (e) => {
              const value = e.target.value;
              if (!value) return;

              if (value === "other") {
                setShowOtherInput(true);
                return;
              }

              setShowOtherInput(false);

              if (!fieldSelectedTrades.includes(value)) {
                form.setFieldValue(
                  "trade_specialization",
                  [...fieldSelectedTrades, value].join(", "),
                );
              }

              if (selectRef.current) {
                selectRef.current.value = "";
              }
            };

            const handleOtherSave = () => {
              if (otherValue.trim()) {
                const newTrades = [
                  ...fieldSelectedTrades.filter((trade) =>
                    TRADE_OPTIONS.some((opt) => opt.value === trade),
                  ),
                  otherValue.trim(),
                ];
                form.setFieldValue("trade_specialization", newTrades.join(", "));
              }
              setShowOtherInput(false);
              if (selectRef.current) {
                selectRef.current.value = "";
              }
            };

            const handleOtherBlur = () => {
              handleOtherSave();
            };

            const isOptionDisabled = (value) => {
              return fieldSelectedTrades.includes(value);
            };

            return (
              <div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {fieldSelectedTrades.map((item) => (
                    <span
                      key={item}
                      className="bg-gray-200 px-3 py-1 rounded-full text-xs flex items-center gap-2"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() =>
                          form.setFieldValue(
                            "trade_specialization",
                            fieldSelectedTrades.filter((v) => v !== item).join(", "),
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>

                <select
                  ref={selectRef}
                  defaultValue=""
                  className="bg-[#F1F6F9] border border-gray-300 rounded px-2 py-1 w-full text-sm focus:border-green-500 focus:outline-none transition outline outline-1 outline-gray-300 border-r-8 border-transparent"
                  onChange={handleSelectChange}
                >
                  <option value="">
                    {t(
                      "contractorSignup.form.step2.tradeSelectPlaceholder",
                      "Select Trade/Specialization",
                    )}
                  </option>
                  {TRADE_OPTIONS.map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={isOptionDisabled(opt.value)}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>

                {showOtherInput && (
                  <div className="relative mt-2">
                    <input
                      className="bg-[#F1F6F9] border border-gray-300 rounded px-2 py-1 w-full text-sm focus:border-green-500 focus:outline-none transition pr-9"
                      placeholder={t(
                        "contractorSignup.form.step2.tradeOtherPlaceholder",
                        "Enter other specialization",
                      )}
                      value={otherValue}
                      onChange={(e) => setOtherValue(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleOtherSave();
                        }
                      }}
                      onBlur={handleOtherBlur}
                    />
                    <svg
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 cursor-pointer hover:opacity-70"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </div>
                )}
                <ErrorMessage
                  name="trade_specialization"
                  component="div"
                  className="text-red-500 text-xs mt-1"
                />
              </div>
            );
          }}
        </Field>
      </div>
      {/* GDPR & Data Processing */}
      <div className="col-span-2">
        <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1">
          {t("contractorSignup.form.step2.gdprLabel", "GDPR & Data Processing")}
        </label>
        <Field name="gdpr_data_processing">
          {({ form, field }) => (
            <div>
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={(event) => {
                  form.setFieldValue("gdpr_data_processing", event.currentTarget.files[0]);
                }}
                className="bg-[#F1F6F9] border border-gray-300 rounded px-2 py-1 w-full text-sm focus:border-green-500 focus:outline-none transition"
              />
              <ErrorMessage
                name="gdpr_data_processing"
                component="div"
                className="text-red-500 text-xs mt-1"
              />
              <div className="mt-2 text-xs text-gray-600 flex gap-2 flex-wrap">
                {field.value ? (
                  <span className="flex items-center gap-1">
                    {field.value.name.endsWith(".pdf") ? (
                      <FaFilePdf className="text-red-600" />
                    ) : (
                      <FaFileWord className="text-blue-600" />
                    )}
                    {field.value.name}
                  </span>
                ) : (
                  <span>{t("contractorSignup.form.noFileUploaded", "No file uploaded.")}</span>
                )}
              </div>
            </div>
          )}
        </Field>
      </div>
    </div>
  );
};

export default Step2;
